import { useState } from 'react';
import Layout from '../components/layout/Layout';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import Button from '../components/common/Button';
import { INPUT_SANITIZERS } from '../utils/validationHelpers';

const UNIT_TYPES = ['box', 'pcs', 'kg', 'ltr', 'carton', 'pallet', 'bag', 'bundle', 'roll', 'meter'];

const MOCK_PRODUCTS = [
  { value: '1', label: 'Steel Rods (SR-2024)' },
  { value: '2', label: 'PVC Pipes (PVC-100)' },
  { value: '3', label: 'Cement Bags (CB-50KG)' },
  { value: '4', label: 'Tiles (TL-60x60)' },
  { value: '5', label: 'Paint Cans (PT-WHITE)' },
  { value: '6', label: 'Wood Panels (WP-12MM)' },
  { value: '7', label: 'Electrical Wire (EW-2.5)' },
  { value: '8', label: 'Bolts & Nuts (BN-M10)' },
];

const MOCK_ZONES = [
  { value: 'Z001', label: 'Zone A - Ground Floor' },
  { value: 'Z002', label: 'Zone B - Cold Storage' },
  { value: 'Z003', label: 'Zone C - Hazardous' },
  { value: 'Z004', label: 'Zone D - Oversized' },
];

const MOCK_RACKS = [
  { value: 'R001', label: 'Rack R01 - Aisle 1' },
  { value: 'R002', label: 'Rack R02 - Aisle 1' },
  { value: 'R003', label: 'Rack R03 - Aisle 2' },
  { value: 'R004', label: 'Rack R04 - Aisle 2' },
];

const MOCK_LOCATIONS: Record<string, { value: string; label: string }[]> = {
  R001: [
    { value: 'L001', label: 'L001 - Row 1 Shelf A' },
    { value: 'L002', label: 'L002 - Row 1 Shelf B' },
    { value: 'L003', label: 'L003 - Row 2 Shelf A' },
  ],
  R002: [
    { value: 'L004', label: 'L004 - Row 1 Shelf A' },
    { value: 'L005', label: 'L005 - Row 1 Shelf B' },
  ],
  R003: [
    { value: 'L006', label: 'L006 - Row 1 Shelf A' },
    { value: 'L007', label: 'L007 - Row 2 Shelf A' },
    { value: 'L008', label: 'L008 - Row 2 Shelf B' },
  ],
  R004: [
    { value: 'L009', label: 'L009 - Row 1 Shelf A' },
    { value: 'L010', label: 'L010 - Row 1 Shelf B' },
  ],
};

interface ProductRow {
  id: string;
  productId: string;
  skuCode: string;
  batchNo: string;
  qty: string;
  unitType: string;
}

interface AssignmentRow {
  productRowId: string;
  palletNo: string;
  placementType: 'zone' | 'rack';
  zoneId: string;
  rackId: string;
  locationId: string;
}

interface SharedPallet {
  palletNo: string;
  placementType: 'zone' | 'rack';
  zoneId: string;
  rackId: string;
  locationId: string;
  productIds: string[];
}

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

function emptyProductRow(): ProductRow {
  return { id: generateId(), productId: '', skuCode: '', batchNo: '', qty: '', unitType: 'box' };
}

function emptyAssignment(productRowId: string): AssignmentRow {
  return { productRowId, palletNo: '', placementType: 'zone', zoneId: '', rackId: '', locationId: '' };
}

export default function PalletAssignment() {
  const [step, setStep] = useState<1 | 2>(1);
  const [productRows, setProductRows] = useState<ProductRow[]>([emptyProductRow()]);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [sharedPallet, setSharedPallet] = useState<SharedPallet>({
    palletNo: '',
    placementType: 'zone',
    zoneId: '',
    rackId: '',
    locationId: '',
    productIds: [],
  });
  const [useSharedPallet, setUseSharedPallet] = useState(false);
  const [productErrors, setProductErrors] = useState<Record<string, Record<string, string>>>({});
  const [submitted, setSubmitted] = useState(false);

  // ---- Step 1 handlers ----

  function updateProductRow(id: string, field: keyof ProductRow, value: string) {
    setProductRows(rows => rows.map(r => r.id === id ? { ...r, [field]: value } : r));
    setProductErrors(errs => {
      const next = { ...errs };
      if (next[id]) delete next[id][field];
      return next;
    });
  }

  function addProductRow() {
    setProductRows(rows => [...rows, emptyProductRow()]);
  }

  function removeProductRow(id: string) {
    setProductRows(rows => rows.filter(r => r.id !== id));
  }

  function validateStep1(): boolean {
    const errors: Record<string, Record<string, string>> = {};
    let valid = true;
    for (const row of productRows) {
      const rowErr: Record<string, string> = {};
      if (!row.productId) rowErr.productId = 'Select a product';
      if (!row.qty || parseFloat(row.qty) <= 0) rowErr.qty = 'Enter valid quantity';
      if (Object.keys(rowErr).length > 0) {
        errors[row.id] = rowErr;
        valid = false;
      }
    }
    setProductErrors(errors);
    return valid;
  }

  function goToStep2() {
    if (!validateStep1()) return;
    const newAssignments = productRows.map(r => emptyAssignment(r.id));
    setAssignments(newAssignments);
    setStep(2);
  }

  // ---- Step 2 handlers ----

  function updateAssignment(productRowId: string, field: keyof AssignmentRow, value: string) {
    setAssignments(rows =>
      rows.map(r => {
        if (r.productRowId !== productRowId) return r;
        if (field === 'rackId') return { ...r, [field]: value, locationId: '' };
        return { ...r, [field]: value };
      })
    );
  }

  function toggleSharedPalletProduct(productRowId: string) {
    setSharedPallet(sp => ({
      ...sp,
      productIds: sp.productIds.includes(productRowId)
        ? sp.productIds.filter(id => id !== productRowId)
        : [...sp.productIds, productRowId],
    }));
  }

  function updateSharedPallet(field: keyof SharedPallet, value: string) {
    setSharedPallet(sp => {
      if (field === 'rackId') return { ...sp, [field]: value, locationId: '' };
      if (field === 'placementType') return { ...sp, [field]: value as 'zone' | 'rack', zoneId: '', rackId: '', locationId: '' };
      return { ...sp, [field]: value };
    });
  }

  function getProductLabel(productId: string) {
    return MOCK_PRODUCTS.find(p => p.value === productId)?.label ?? '—';
  }

  function isProductOnSharedPallet(productRowId: string) {
    return useSharedPallet && sharedPallet.productIds.includes(productRowId);
  }

  function handleConfirm() {
    setSubmitted(true);
  }

  // ---- Derived ----

  const canProceed = productRows.length > 0 && productRows.some(r => r.productId);

  if (submitted) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <i className="fas fa-check text-green-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-800">Assignment Confirmed</h2>
          <p className="text-gray-500 text-sm text-center max-w-sm">
            {productRows.length} product{productRows.length > 1 ? 's have' : ' has'} been assigned to their locations successfully.
          </p>
          <Button
            variant="primary"
            icon="fa-plus"
            onClick={() => {
              setStep(1);
              setProductRows([emptyProductRow()]);
              setAssignments([]);
              setSharedPallet({ palletNo: '', placementType: 'zone', zoneId: '', rackId: '', locationId: '', productIds: [] });
              setUseSharedPallet(false);
              setProductErrors({});
              setSubmitted(false);
            }}
          >
            New Assignment
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Pallet Assignment</h1>
          <p className="text-sm text-gray-500 mt-1">Add products and assign them to warehouse locations or pallets</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 1 ? 'bg-brand-600 text-white' : 'bg-green-500 text-white'}`}>
              {step === 1 ? '1' : <i className="fas fa-check text-xs"></i>}
            </div>
            <span className={`text-sm font-medium ${step === 1 ? 'text-brand-600' : 'text-green-600'}`}>Add Products</span>
          </div>
          <div className={`flex-1 mx-4 h-0.5 ${step === 2 ? 'bg-brand-600' : 'bg-gray-200'}`}></div>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 2 ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
              2
            </div>
            <span className={`text-sm font-medium ${step === 2 ? 'text-brand-600' : 'text-gray-400'}`}>Assign Locations</span>
          </div>
        </div>

        {/* ===== STEP 1 ===== */}
        {step === 1 && (
          <div>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-800">Products</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Add all products you want to assign in this session</p>
                </div>
                <Button variant="outline" size="sm" icon="fa-plus" onClick={addProductRow}>
                  Add Product
                </Button>
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-12 gap-3 px-5 py-2.5 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="col-span-1">#</div>
                <div className="col-span-4">Product</div>
                <div className="col-span-2">SKU / Item Code</div>
                <div className="col-span-2">Batch No.</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-1"></div>
              </div>

              {/* Product Rows */}
              <div className="divide-y divide-gray-50">
                {productRows.map((row, index) => (
                  <div key={row.id} className="grid grid-cols-12 gap-3 px-5 py-3 items-start">
                    <div className="col-span-1 flex items-center h-9">
                      <span className="text-sm text-gray-400 font-medium">{index + 1}</span>
                    </div>

                    <div className="col-span-4">
                      <ResponsiveSelect
                        options={MOCK_PRODUCTS}
                        value={row.productId}
                        onChange={val => updateProductRow(row.id, 'productId', val)}
                        placeholder="Select product..."
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                          productErrors[row.id]?.productId
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand-500'
                        }`}
                      />
                      {productErrors[row.id]?.productId && (
                        <p className="mt-1 text-xs text-red-600">{productErrors[row.id].productId}</p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <input
                        type="text"
                        placeholder="SKU code"
                        value={row.skuCode}
                        onChange={e => updateProductRow(row.id, 'skuCode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>

                    <div className="col-span-2">
                      <input
                        type="text"
                        placeholder="Batch no."
                        value={row.batchNo}
                        onChange={e => updateProductRow(row.id, 'batchNo', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>

                    <div className="col-span-1">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="0"
                        value={row.qty}
                        onChange={e => updateProductRow(row.id, 'qty', INPUT_SANITIZERS.digitsOnly(e.target.value))}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
                          productErrors[row.id]?.qty
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand-500'
                        }`}
                      />
                      {productErrors[row.id]?.qty && (
                        <p className="mt-1 text-xs text-red-600">{productErrors[row.id].qty}</p>
                      )}
                    </div>

                    <div className="col-span-1">
                      <ResponsiveSelect
                        options={UNIT_TYPES.map(u => ({ value: u, label: u }))}
                        value={row.unitType}
                        onChange={val => updateProductRow(row.id, 'unitType', val)}
                        includeEmpty={false}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>

                    <div className="col-span-1 flex items-center justify-center h-9">
                      <button
                        onClick={() => removeProductRow(row.id)}
                        disabled={productRows.length === 1}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <i className="fas fa-trash-alt text-xs"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add row footer */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                <button
                  onClick={addProductRow}
                  className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium"
                >
                  <i className="fas fa-plus text-xs"></i>
                  Add another product
                </button>
              </div>
            </div>

            {/* Summary bar */}
            {productRows.some(r => r.productId) && (
              <div className="mt-4 px-4 py-3 bg-brand-50 border border-brand-100 rounded-lg flex items-center gap-2">
                <i className="fas fa-info-circle text-brand-500 text-sm"></i>
                <span className="text-sm text-brand-700">
                  <span className="font-semibold">{productRows.filter(r => r.productId).length}</span> product{productRows.filter(r => r.productId).length > 1 ? 's' : ''} ready to be assigned
                </span>
              </div>
            )}

            {/* Footer actions */}
            <div className="mt-6 flex justify-end">
              <Button
                variant="primary"
                icon="fa-arrow-right"
                iconPosition="right"
                disabled={!canProceed}
                onClick={goToStep2}
              >
                Next: Assign Locations
              </Button>
            </div>
          </div>
        )}

        {/* ===== STEP 2 ===== */}
        {step === 2 && (
          <div className="space-y-5">

            {/* Shared Pallet Panel */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setUseSharedPallet(v => !v)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${useSharedPallet ? 'bg-brand-600 border-brand-600' : 'border-gray-400'}`}>
                    {useSharedPallet && <i className="fas fa-check text-white text-xs"></i>}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800">Assign multiple products to one shared pallet</p>
                    <p className="text-xs text-gray-500 mt-0.5">Select products to group on a single pallet, then assign the pallet to a location</p>
                  </div>
                </div>
                <i className={`fas fa-chevron-${useSharedPallet ? 'up' : 'down'} text-gray-400 text-sm`}></i>
              </button>

              {useSharedPallet && (
                <div className="border-t border-gray-100 px-5 py-5 space-y-5">
                  {/* Pallet details row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Pallet Number *"
                      placeholder="e.g. PLT-0001"
                      value={sharedPallet.palletNo}
                      onChange={e => updateSharedPallet('palletNo', e.target.value)}
                      icon="fa-pallet"
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Placement Type</label>
                      <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                        <button
                          onClick={() => updateSharedPallet('placementType', 'zone')}
                          className={`flex-1 py-2 text-sm font-medium transition-colors ${sharedPallet.placementType === 'zone' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                          <i className="fas fa-layer-group mr-1.5"></i> Zone
                        </button>
                        <button
                          onClick={() => updateSharedPallet('placementType', 'rack')}
                          className={`flex-1 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${sharedPallet.placementType === 'rack' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                          <i className="fas fa-th-large mr-1.5"></i> Rack
                        </button>
                      </div>
                    </div>

                    {sharedPallet.placementType === 'zone' ? (
                      <Select
                        label="Zone *"
                        options={MOCK_ZONES}
                        value={sharedPallet.zoneId}
                        onChange={e => updateSharedPallet('zoneId', e.target.value)}
                        placeholder="Select zone..."
                      />
                    ) : (
                      <Select
                        label="Rack *"
                        options={MOCK_RACKS}
                        value={sharedPallet.rackId}
                        onChange={e => updateSharedPallet('rackId', e.target.value)}
                        placeholder="Select rack..."
                      />
                    )}

                    {sharedPallet.placementType === 'rack' && sharedPallet.rackId && (
                      <Select
                        label="Location *"
                        options={MOCK_LOCATIONS[sharedPallet.rackId] ?? []}
                        value={sharedPallet.locationId}
                        onChange={e => updateSharedPallet('locationId', e.target.value)}
                        placeholder="Select location..."
                      />
                    )}
                  </div>

                  {/* Select products for shared pallet */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Select products for this pallet</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {productRows.filter(r => r.productId).map(row => (
                        <label
                          key={row.id}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-colors ${
                            sharedPallet.productIds.includes(row.id)
                              ? 'border-brand-500 bg-brand-50'
                              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={sharedPallet.productIds.includes(row.id)}
                            onChange={() => toggleSharedPalletProduct(row.id)}
                            className="w-4 h-4 text-brand-600 rounded border-gray-300"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{getProductLabel(row.productId)}</p>
                            <p className="text-xs text-gray-500">{row.qty} {row.unitType}{row.batchNo ? ` · ${row.batchNo}` : ''}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Individual Assignment Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-800">
                  {useSharedPallet ? 'Individual Assignments' : 'Product Location Assignment'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {useSharedPallet
                    ? 'Products not on the shared pallet — assign them individually below'
                    : 'Assign each product to a zone or rack location'}
                </p>
              </div>

              <div className="divide-y divide-gray-50">
                {productRows.filter(r => r.productId).map((row, index) => {
                  const assignment = assignments.find(a => a.productRowId === row.id)!;
                  const onSharedPallet = isProductOnSharedPallet(row.id);

                  return (
                    <div key={row.id} className={`px-5 py-4 ${onSharedPallet ? 'opacity-40 pointer-events-none' : ''}`}>
                      <div className="flex items-start gap-4">
                        {/* Product info */}
                        <div className="flex-shrink-0 w-6 h-6 mt-0.5 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-semibold text-gray-500">{index + 1}</span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-gray-800">{getProductLabel(row.productId)}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {row.qty} {row.unitType}
                                {row.skuCode ? ` · ${row.skuCode}` : ''}
                                {row.batchNo ? ` · Batch: ${row.batchNo}` : ''}
                              </p>
                            </div>

                            {onSharedPallet && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-100 text-brand-700 text-xs font-medium">
                                <i className="fas fa-pallet text-xs"></i>
                                Shared pallet
                              </span>
                            )}
                          </div>

                          {!onSharedPallet && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Pallet No. (optional)</label>
                                <input
                                  type="text"
                                  placeholder="e.g. PLT-0002"
                                  value={assignment?.palletNo ?? ''}
                                  onChange={e => updateAssignment(row.id, 'palletNo', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                />
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Placement</label>
                                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                  <button
                                    onClick={() => updateAssignment(row.id, 'placementType', 'zone')}
                                    className={`flex-1 py-2 text-xs font-medium transition-colors ${assignment?.placementType === 'zone' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                  >
                                    Zone
                                  </button>
                                  <button
                                    onClick={() => updateAssignment(row.id, 'placementType', 'rack')}
                                    className={`flex-1 py-2 text-xs font-medium border-l border-gray-300 transition-colors ${assignment?.placementType === 'rack' ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                  >
                                    Rack
                                  </button>
                                </div>
                              </div>

                              {assignment?.placementType === 'zone' ? (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Zone</label>
                                  <ResponsiveSelect
                                    options={MOCK_ZONES}
                                    value={assignment.zoneId}
                                    onChange={val => updateAssignment(row.id, 'zoneId', val)}
                                    placeholder="Select zone..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                  />
                                </div>
                              ) : (
                                <>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Rack</label>
                                    <ResponsiveSelect
                                      options={MOCK_RACKS}
                                      value={assignment?.rackId ?? ''}
                                      onChange={val => updateAssignment(row.id, 'rackId', val)}
                                      placeholder="Select rack..."
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                                    <ResponsiveSelect
                                      options={assignment?.rackId ? MOCK_LOCATIONS[assignment.rackId] ?? [] : []}
                                      value={assignment?.locationId ?? ''}
                                      onChange={val => updateAssignment(row.id, 'locationId', val)}
                                      placeholder="Select location..."
                                      disabled={!assignment?.rackId}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Assignment summary */}
            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Assignment Summary</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <span><span className="font-semibold text-gray-900">{productRows.filter(r => r.productId).length}</span> products total</span>
                {useSharedPallet && sharedPallet.productIds.length > 0 && (
                  <span><span className="font-semibold text-brand-600">{sharedPallet.productIds.length}</span> on shared pallet</span>
                )}
                <span>
                  <span className="font-semibold text-gray-900">
                    {assignments.filter(a => {
                      if (isProductOnSharedPallet(a.productRowId)) return false;
                      return a.zoneId || a.locationId;
                    }).length}
                  </span> individually assigned
                </span>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                icon="fa-arrow-left"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
              <Button
                variant="success"
                icon="fa-check"
                onClick={handleConfirm}
              >
                Confirm Assignment
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
